import {
  Box,
  Collapse,
  darkTheme,
  List,
  ListItemButton,
  Paper,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@compass/core";
import { ThemeProvider } from "@mui/material/styles";
import { useNavigationBar } from "hooks";
import produce, { original } from "immer";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setAppNavDrawerOpen } from "redux/reducers/appSlice";
import { setUserSettingNavPinned } from "redux/reducers/userSettingSlice";
import { RootState } from "redux/store";

import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import { NavMenuItem, NavSubMenuItem, NavSubSubMenuItem } from "routes";
import ApiService from "services/apiService";

export const MainBar = (props: {
  width: number;
  navMenuItems: NavMenuItem[];
  setNavMenuItems: (value: NavMenuItem[]) => void;
}) => {
  const theme = useTheme();

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const navPinned = useSelector((state: RootState) => state.userSetting.navPinned);
  const { showPin } = useNavigationBar();
  const navigationBarRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const handleMenuItemClick = (menuItem: NavMenuItem) => {
    if (menuItem.subMenuItems.length > 1) {
      props.setNavMenuItems(
        produce(props.navMenuItems, (draft) => {
          let draftMenuItem = draft?.find((x) => original(x) === menuItem)!;
          draftMenuItem.expanded = !draftMenuItem.expanded;
        })
      );
    } else {
      handleSubMenuItemClick(menuItem.subMenuItems[0]);
    }
  };

  const handleSubMenuItemClick = (subMenuItem: NavSubMenuItem) => {
    if (subMenuItem.subSubMenuItems && subMenuItem.subSubMenuItems.length > 0) {
      props.setNavMenuItems(
        produce(props.navMenuItems, (draft) => {
          let draftMenuItem = draft?.find((x) => original(x)?.subMenuItems.includes(subMenuItem))!;
          let draftSubMenuItem = draftMenuItem?.subMenuItems?.find((x) => original(x) === subMenuItem)!;
          draftSubMenuItem.expanded = !draftSubMenuItem.expanded;
        })
      );
    } else {
      navigate(subMenuItem.path!);
      dispatch(setAppNavDrawerOpen(false));
    }
  };

  const handleSubSubMenuItemClick = (subSubMenuItem: NavSubSubMenuItem) => {
    navigate(subSubMenuItem.path!);
    dispatch(setAppNavDrawerOpen(false));
  };

  const renderMenuItems = () => {
    return (
      <Box>
        <Box mt={2}>
          <List>
            {props.navMenuItems.map((menuItem) => {
              const selected =
                menuItem.subMenuItems?.find(
                  (x) =>
                    (x.path && pathname.includes(x.path)) ||
                    x.subSubMenuItems?.find((y) => pathname.includes(y.path)) !== undefined
                ) !== undefined;

              return (
                <Box key={menuItem.title}>
                  <ListItemButton
                    sx={{ display: "block" }}
                    onClick={() => handleMenuItemClick(menuItem)}
                    selected={selected}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" px={1} minHeight="40px">
                      <Stack direction="row" spacing="sm" alignItems="center">
                        {menuItem.icon}
                        <Typography fontWeight="medium">{menuItem.title}</Typography>
                      </Stack>

                      {menuItem.subMenuItems.length > 1 && (
                        <ExpandCircleDownIcon
                          sx={{
                            color: menuItem.expanded ? "#90D5FF" : undefined,
                            transform: menuItem.expanded ? "rotate(0deg)" : "rotate(180deg)",
                            transition: "all 0.2s",
                          }}
                        />
                      )}
                    </Stack>
                  </ListItemButton>

                  {renderSubMenuItems(menuItem.subMenuItems, menuItem.expanded)}
                </Box>
              );
            })}
          </List>
        </Box>
      </Box>
    );
  };

  const renderSubMenuItems = (subMenuItems: NavSubMenuItem[] | undefined, expanded: boolean | undefined) => {
    if (subMenuItems && subMenuItems.length > 1) {
      return (
        <Collapse in={expanded} orientation="vertical">
          <Box px={3} fontSize="0.9rem">
            <List>
              {subMenuItems.map((subMenuItem) => {
                const selected =
                  (subMenuItem.path && pathname.includes(subMenuItem.path)) ||
                  subMenuItem.subSubMenuItems?.find((x) => pathname.includes(x.path)) !== undefined;

                return (
                  <Box key={subMenuItem.title}>
                    <ListItemButton
                      key={subMenuItem.title}
                      onClick={() => handleSubMenuItemClick(subMenuItem)}
                      selected={selected}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="body1">{subMenuItem.title}</Typography>

                        {subMenuItem.subSubMenuItems && subMenuItem.subSubMenuItems.length > 0 && (
                          <KeyboardArrowUpIcon
                            sx={{
                              transform: subMenuItem.expanded ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "all 0.2s",
                            }}
                          />
                        )}
                      </Stack>
                    </ListItemButton>

                    {renderSubSubMenuItems(subMenuItem.subSubMenuItems, subMenuItem.expanded)}
                  </Box>
                );
              })}
            </List>
          </Box>
        </Collapse>
      );
    }

    return null;
  };

  const renderSubSubMenuItems = (subSubMenuItems: NavSubSubMenuItem[] | undefined, expanded: boolean | undefined) => {
    if (subSubMenuItems && subSubMenuItems.length > 0) {
      return (
        <Collapse in={expanded} orientation="vertical">
          <Box px={3} fontSize="0.9rem">
            <List>
              {subSubMenuItems?.map((subSubMenuItem) => {
                const selected = pathname.includes(subSubMenuItem.path);

                return (
                  <ListItemButton
                    key={subSubMenuItem.title}
                    onClick={() => handleSubSubMenuItemClick(subSubMenuItem)}
                    selected={selected}
                  >
                    <Typography variant="body2">{subSubMenuItem.title}</Typography>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        </Collapse>
      );
    }

    return null;
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Paper
        ref={navigationBarRef}
        sx={{
          position: "relative",
          height: "100vh",
          width: props.width,
          borderRadius: "0px !important",
          overflow: "auto",
          flexShrink: 0,
          zIndex: theme.zIndex.appBar,
          // "&:hover": {
          //   overflow: "auto"
          // }
        }}
      >
        {showPin && (
          <Stack p={2}>
            <Switch
              onClick={() => {
                const setValue: boolean = !navPinned;
                dispatch(setUserSettingNavPinned(setValue));
                ApiService.userSetting.saveNavPinned(setValue);
              }}
              checked={navPinned}
            ></Switch>
          </Stack>
        )}

        {renderMenuItems()}
      </Paper>
    </ThemeProvider>
  );
};
